import { memo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { CustomButton } from "../../components/common/customButton";
import { renderSubTitle } from "../../components/renderComponents";
import { useDealProvider } from "../../context/DealContext";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { apiGetFile } from "../../services/genesisApi/httpsMethods";
import { parseDealExcelUpload } from "./parseDealExcelUpload";

/**
 * Excel upload UX:
 * - Download the tenancy schedule template (deal-specific when editing)
 * - Choose an .xlsx/.xls file
 * - Parse Assets + Tenants sheets into the deal tables
 */
const DealExcelUpload = ({ dealId = null }: { dealId?: number | null }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { setAssets, setTenants } = useDealProvider();
  const { countries, sectors } = useReferenceLists();

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setStatusMessage(null);
    setErrorMessage(null);
    // Allow selecting the same file again later
    event.target.value = "";
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    setErrorMessage(null);
    setStatusMessage(null);

    try {
      const templatePath = dealId
        ? `tenancy-schedule-template?deal_id=${dealId}`
        : "tenancy-schedule-template";
      const response = await apiGetFile(templatePath);
      if (!response.success || !response.data) {
        setErrorMessage("Failed to download template. Please try again.");
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "tenancy-schedule-template.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
      setStatusMessage("Template downloaded.");
    } catch (error) {
      console.error("Error downloading template:", error);
      setErrorMessage("Failed to download template. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleParseExcel = async () => {
    if (!selectedFile) return;

    setIsParsing(true);
    setErrorMessage(null);
    setStatusMessage(null);

    try {
      const result = await parseDealExcelUpload(
        selectedFile,
        countries || [],
        sectors || [],
      );

      if (result.sheetsFound.length === 0) {
        setErrorMessage(
          'No valid sheets found. Please ensure your Excel file contains "Assets" and/or "Tenants" sheets.',
        );
        return;
      }

      if (result.assets.length > 0) {
        const assetsWithDeal = dealId
          ? result.assets.map((asset: any) => ({
              ...asset,
              deal: { id: dealId },
              deal_id: dealId,
            }))
          : result.assets;
        setAssets(assetsWithDeal as any);
      }
      if (result.tenants.length > 0) {
        setTenants(result.tenants as any);
      }

      setStatusMessage(`Successfully parsed: ${result.sheetsFound.join(", ")}`);
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      setErrorMessage(
        "Error parsing Excel file. Please ensure it's a valid Excel file with the correct sheet names.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
      }}
    >
      {renderSubTitle("Upload Excel Template")}

      <Typography sx={{ fontSize: "14px", color: "#55575C", mb: 2, mt: 1 }}>
        Important: If you have updated a deal you must always download a new
        excel template rather than trying to re-upload an outdated one.
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".xlsx,.xls"
        onChange={handleFileChange}
      />

      <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <CustomButton
          text="Choose Excel File"
          handleButtonPress={handleChooseFile}
          isButtonDisabled={isParsing || isDownloading}
          solid={false}
          widthOverride="150px"
        />
        <CustomButton
          text={isDownloading ? "Downloading..." : "Download Template"}
          handleButtonPress={handleDownloadTemplate}
          isButtonDisabled={isParsing || isDownloading}
          solid={true}
          widthOverride="170px"
        />
      </Box>

      {selectedFile && (
        <>
          <Typography sx={{ fontSize: "14px", color: "#55575C", mb: 2 }}>
            {selectedFile.name}
          </Typography>
          <CustomButton
            text={isParsing ? "Parsing..." : "Parse Excel File"}
            handleButtonPress={handleParseExcel}
            isButtonDisabled={isParsing || isDownloading}
            solid={true}
            widthOverride="160px"
          />
        </>
      )}

      {statusMessage && (
        <Typography sx={{ mt: 2, fontSize: "14px", color: "#1B7A63" }}>
          {statusMessage}
        </Typography>
      )}
      {errorMessage && (
        <Typography sx={{ mt: 2, fontSize: "14px", color: "#d32f2f" }}>
          {errorMessage}
        </Typography>
      )}
    </Paper>
  );
};

// Memoized so parent forms (e.g. CreateDealUpdate) can re-render on each
// keystroke without re-rendering this component.
export default memo(DealExcelUpload);
